import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { OwnerStmtService } from '@app/_components/owner-statements/owner-stmt.service';

interface DashboardData {
  month: number;
  year: number;
  startDate: string;
  endDate: string;
  expensesReadyToAddCount: number;
  expensesReadyToAddTotal: number;
  expensesReadyToAddStmts: number;
  reservationsReadyToAddCount: number;
  reservationsReadyToAddStmts: number;
  expenseBlockersNullBilledDate: number;
  expenseBlockersNotReviewed: number;
  expenseBlockersStmts: number;
  unlockedLedgerReservations: number;
  oxAwaitingManualAdd: number;
  statusDefault: number;
  statusDraft: number;
  statusNeedAttention: number;
  statusReadyForReview1: number;
  statusReadyForReview2: number;
  statusReadyForReview3: number;
  statusApproved: number;
  statusPublished: number;
  statusTotal: number;
  emailedCount: number;
  emailRemaining: number;
  payPaid: number;
  paySubmitted: number;
  payQueued: number;
  payPending: number;
  payReturned: number;
  payFailed: number;
  payManualPaid: number;
  payNotApplicable: number;
  payUnset: number;
  nachaBatchId: number | null;
  nachaBatchStatus: string | null;
  nachaEntryCount: number;
  nachaTotalAmount: number;
  nachaEffectiveDate: string | null;
  lastCreated: string | null;
  lastUpdated: string | null;
  pendingBillingNullDate: number;
  pendingBillingOldestAgeDays: number;
  missingLedgerRowsReservations: number;
  lastRunPaidNightsZero: number;
  lastRunInvariantR7: number;
  lastRunExpenseErrors: number;
  lastRunTimestamp: string | null;
}

interface TileInfo {
  title: string;
  what: string;
  why: string;
  greenWhen: string;
  yellowWhen?: string;
  redWhen: string;
  whatToDo: string;
}

@Component({
  selector: 'app-statement-dashboard',
  templateUrl: './statement-dashboard.component.html',
  styleUrls: ['./statement-dashboard.component.scss'],
  providers: [ConfirmationService],
  standalone: false
})
export class StatementDashboardComponent implements OnChanges {

  @Input() month: number;
  @Input() year: number;

  @Output() filterByStatus = new EventEmitter<number[]>();
  @Output() showOnlyUnemailed = new EventEmitter<void>();
  @Output() reloadStatementsList = new EventEmitter<void>();
  /** Emits the count of critical blockers (expense blockers + unlocked + missing ledger rows) each time the dashboard refreshes. */
  @Output() criticalBlockerCount = new EventEmitter<number>();

  data: DashboardData | null = null;
  loading = false;
  actionInFlight = false;

  // Drill-down dialog state
  drillDialogOpen = false;
  drillTitle = '';
  drillRows: any[] = [];
  drillColumns: { field: string; header: string }[] = [];
  drillTileKey: string = '';
  drillLoading = false;
  drillError: string | null = null;

  // Info popover state
  infoOpen: { [key: string]: boolean } = {};

  // Plain-English explanations for every tile (visible via info icon)
  readonly TILE_INFO: { [key: string]: TileInfo } = {
    reservationsReady: {
      title: 'Reservations Ready to Add',
      what: 'Bookings that fall in this statement period but are NOT YET on the owner statement.',
      why: 'Statements are usually generated mid-month. Any booking made after that point is missing from the statement until you regenerate. This is normal and expected.',
      greenWhen: 'Zero bookings waiting — all reservations in the period are on the statement.',
      yellowWhen: 'One or more bookings are waiting to be pulled in. Not a problem yet — just click the button.',
      redWhen: 'Never red. This is always actionable with one click.',
      whatToDo: 'Click "Regenerate Statements" on the tile. The system pulls in the new bookings. Published statements are NEVER touched.'
    },
    expensesReady: {
      title: 'Expenses Ready to Add',
      what: 'Reviewed expenses (work orders, vendor charges) that fall in this period but are NOT YET on the owner statement.',
      why: 'Expenses must be in REVIEWED status before they appear on a statement. Once Bookkeeping advances them, they need to be pulled into existing statements.',
      greenWhen: 'Zero expenses waiting — all REVIEWED expenses are already on statements.',
      yellowWhen: 'Reviewed expenses are waiting. Click the button to add them.',
      redWhen: 'Never red.',
      whatToDo: 'Click "Regenerate Expenses". Adds them to non-Published statements only.'
    },
    expenseBlockers: {
      title: 'Expense Blockers',
      what: 'Expenses that SHOULD be on the statement but won\'t appear because of a data problem: either the Statement Billed Date is missing, OR the status is not REVIEWED.',
      why: 'These are silent. The statement won\'t show them unless you fix them first. A common cause: Breezeway-imported expenses arrive at APPROVED status — Bookkeeping must advance to REVIEWED.',
      greenWhen: 'Zero blockers — all expenses for the period are properly set up.',
      redWhen: 'One or more blockers exist. Owner statements will be missing these expenses until resolved.',
      whatToDo: 'Click the tile to see the list. For "Not Reviewed" expenses, you can use the "Bulk Set REVIEWED" button to advance all eligible expenses at once. For "NULL Billed Date" expenses, open the expense and set the Statement Billed Date manually.'
    },
    unlockedLedgers: {
      title: 'Unlocked Reservations',
      what: 'Reservations whose ledger rows are NOT locked. The statement generator silently SKIPS them — the entire reservation is excluded from the statement.',
      why: 'When a guest adds an item (rollaway, crib) on the website OR an admin adds a line on the reservation, the new ledger row was inserted unlocked. As of 2026-04-26, new rows auto-lock — but pre-existing unlocked rows still need cleanup.',
      greenWhen: 'Zero unlocked — all reservations in the period are eligible for the statement.',
      redWhen: 'One or more reservations have unlocked rows. They will be SILENTLY EXCLUDED from the statement, robbing the owner of revenue.',
      whatToDo: 'Click the tile to see the affected reservations. Click "Lock All in Period" to bulk-lock every unlocked row, then click "Regenerate Statements" on the Reservations tile.'
    },
    oxAwaiting: {
      title: 'Cancelled (OX) Awaiting',
      what: 'Cancelled bookings where KBM kept money (net retained > $0) AND the unit did NOT re-book. The owner is owed this cancellation revenue.',
      why: 'Cancellation revenue belongs to the owner ONLY when (1) we kept the guest\'s payment AND (2) we didn\'t re-rent the dates. Otherwise the owner has no claim, or the replacement reservation will pay them.',
      greenWhen: 'Zero qualifying cancellations — owner is not missing any cancellation revenue.',
      redWhen: 'One or more cancellations qualify but are NOT on the statement. The owner is missing revenue.',
      whatToDo: 'Click the tile to see the list with net retained amount. For each, manually add via the reservation page (or wait for Phase 2 auto-include).'
    },
    email: {
      title: 'Email Progress',
      what: 'How many Published statements have been emailed to owners.',
      why: 'Statements only count as delivered once the owner has received the PDF email.',
      greenWhen: 'All Published statements have been emailed.',
      yellowWhen: 'Some Published statements still need to be emailed.',
      redWhen: 'Not used.',
      whatToDo: 'Click the tile to filter the list to unemailed Published statements. Then use the red "Bulk Mail Statements" button at the top of the table.'
    },
    payments: {
      title: 'Payments',
      what: 'How many statement payments have CLEARED via NACHA / ACH (or manual check/wire).',
      why: 'Owner is only fully served when statements are emailed AND payment has settled in their bank account.',
      greenWhen: 'Every payable statement (positive net proceed) has been PAID.',
      yellowWhen: 'Some are paid, some are still in flight (SUBMITTED, awaiting bank settlement).',
      redWhen: 'Zero paid yet, OR there are RETURNED / FAILED payments that need attention.',
      whatToDo: 'For ACH: Generate the NACHA Distribution file, Jacki uploads to Bank of Hawaii, the system auto-marks PAID after the effective date. For owners without bank info: cut a manual check, then mark the statement MANUAL_PAID with the check number. Click the tile to drill into details.'
    },
    pendingBilling: {
      title: 'Pending Billing (NULL Date)',
      what: 'Expenses with NULL Statement Billed Date. They have not yet been assigned to a statement period — usually because the invoice has not arrived yet, or the work is not complete.',
      why: 'When you open a work order TODAY but the invoice will not arrive for 3 months, the expense lives here until then. This is intentional. Once the invoice arrives, Bookkeeping sets the Statement Billed Date and the expense lands on the correct month\'s statement.',
      greenWhen: 'Zero pending — every expense has a billing target.',
      yellowWhen: 'Some expenses are still in progress. Normal for work orders that span multiple months.',
      redWhen: 'Never red on its own. But if "Oldest Age" gets large (90+ days), one of these expenses may have been forgotten.',
      whatToDo: 'Click "View List" to see all NULL-date expenses ordered by oldest first. Look for old ones that should have been billed by now. Once the vendor invoice arrives, edit the expense and set the Statement Billed Date to the appropriate month.'
    },
    missingLedgerRows: {
      title: 'Missing Required Ledger Rows',
      what: 'Reservations that don\'t have an OWNER_AMOUNT or KBM_MANAGEMENT_FEE ledger row. These reservations will be SILENTLY EXCLUDED from owner statements due to a NullPointerException in the generator.',
      why: 'Every reservation must have these two ledger rows so the system knows how to split revenue between owner and KBM. Without them, the generator fails on this reservation and skips it without warning the user.',
      greenWhen: 'Zero — all reservations have the required rows.',
      redWhen: 'One or more reservations are missing rows. Owner statement WILL be incomplete.',
      whatToDo: 'Click "View List" to see affected reservations. For each, open the reservation and re-trigger ledger generation (the system rebuilds from order details). If order details are also missing, escalate — there may be a deeper data issue.'
    },
    lastRunErrors: {
      title: 'Last Run Errors',
      what: 'Diagnostic counters from the most recent statement generation: paidNights=0 boundary cases, R7 invariant violations, and per-expense exceptions caught during regeneration.',
      why: 'Generation errors used to be invisible — they were logged on the server but never shown to staff. This tile makes them visible so silent skips can be investigated.',
      greenWhen: 'Zero — last run was clean.',
      yellowWhen: 'Edge cases occurred. Usually safe to ignore unless the count is large or growing month over month.',
      redWhen: 'Errors occurred during last run. Some reservations or expenses may be missing from the statements.',
      whatToDo: 'Click "View List" to see what was skipped and why. Investigate any unexpected items, fix the root cause, then run Regenerate again.'
    },
    nacha: {
      title: 'NACHA Distribution',
      what: 'Status of the monthly NACHA batch file used to send ACH payments to all owners at once via Bank of Hawaii.',
      why: 'NACHA is the bulk ACH payment file. One file pays everyone. Without it, no automated payments happen.',
      greenWhen: 'Batch is SUBMITTED to Bank of Hawaii.',
      yellowWhen: 'Batch is GENERATED but not yet submitted (Jacki needs to upload + approve).',
      redWhen: 'No batch generated yet for this period.',
      whatToDo: 'Click "Generate NACHA File" to build the .ach file. Jacki uploads it to BOH Payments Management portal and approves. After upload, click "Mark Submitted" so the dashboard tile turns green.'
    }
  };

  constructor(
    private ownerStmtService: OwnerStmtService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['month'] || changes['year']) && this.month && this.year) {
      this.fetch();
    }
  }

  fetch() {
    if (!this.month || !this.year) return;
    this.loading = true;
    this.ownerStmtService.getDashboard(this.month, this.year).subscribe({
      next: (res: any) => {
        this.data = (res?.data ?? res) as DashboardData;
        this.loading = false;
        const blockers = (this.data.expenseBlockersNullBilledDate || 0)
                       + (this.data.expenseBlockersNotReviewed || 0)
                       + (this.data.unlockedLedgerReservations || 0)
                       + (this.data.missingLedgerRowsReservations || 0);
        this.criticalBlockerCount.emit(blockers);
      },
      error: (err) => {
        console.error('Dashboard fetch failed', err);
        this.loading = false;
      }
    });
  }

  // ---- Tile color helpers ----
  reservationsReadyColor(): string {
    if (!this.data) return 'gray';
    return this.data.reservationsReadyToAddCount > 0 ? 'yellow' : 'green';
  }
  expensesReadyColor(): string {
    if (!this.data) return 'gray';
    return this.data.expensesReadyToAddCount > 0 ? 'yellow' : 'green';
  }
  blockersColor(): string {
    if (!this.data) return 'gray';
    const total = (this.data.expenseBlockersNullBilledDate || 0)
                + (this.data.expenseBlockersNotReviewed || 0);
    return total > 0 ? 'red' : 'green';
  }
  unlockedColor(): string {
    if (!this.data) return 'gray';
    return this.data.unlockedLedgerReservations > 0 ? 'red' : 'green';
  }
  oxColor(): string {
    if (!this.data) return 'gray';
    return this.data.oxAwaitingManualAdd > 0 ? 'red' : 'green';
  }
  emailColor(): string {
    if (!this.data) return 'gray';
    if (this.data.statusPublished === 0) return 'gray';
    return this.data.emailRemaining > 0 ? 'yellow' : 'green';
  }

  /**
   * Payment color (CORRECTED 2026-04-26):
   *  - Red: returns/failures present, OR positive-net Published exist with zero paid
   *  - Yellow: some paid, some in flight
   *  - Green: every payable statement is paid
   *  - Gray: no Published yet
   */
  paymentColor(): string {
    if (!this.data) return 'gray';
    if (this.data.statusPublished === 0) return 'gray';
    const issues = (this.data.payReturned || 0) + (this.data.payFailed || 0);
    if (issues > 0) return 'red';
    const payable = (this.data.statusPublished || 0) - (this.data.payNotApplicable || 0);
    if (payable <= 0) return 'green';
    const paid = (this.data.payPaid || 0) + (this.data.payManualPaid || 0);
    if (paid >= payable) return 'green';
    if (paid === 0) return 'red';
    return 'yellow';
  }

  pendingBillingColor(): string {
    // Always informational — not a "blocker." But if oldest age > 90 days, surface as yellow.
    if (!this.data) return 'gray';
    if (this.data.pendingBillingNullDate === 0) return 'green';
    if (this.data.pendingBillingOldestAgeDays > 90) return 'yellow';
    return 'gray';  // normal informational state
  }

  missingLedgerColor(): string {
    if (!this.data) return 'gray';
    return this.data.missingLedgerRowsReservations > 0 ? 'red' : 'green';
  }

  lastRunErrorsColor(): string {
    if (!this.data) return 'gray';
    const total = (this.data.lastRunPaidNightsZero || 0)
                + (this.data.lastRunInvariantR7 || 0)
                + (this.data.lastRunExpenseErrors || 0);
    if (total === 0) return 'green';
    // R7 or E5 are real errors, R5 is just an edge case
    if ((this.data.lastRunInvariantR7 || 0) > 0 || (this.data.lastRunExpenseErrors || 0) > 0) return 'red';
    return 'yellow';
  }

  nachaColor(): string {
    if (!this.data || !this.data.nachaBatchStatus) return 'red';
    if (this.data.nachaBatchStatus === 'SUBMITTED') return 'green';
    return 'yellow';
  }

  publishedRatio(): string {
    if (!this.data || this.data.statusTotal === 0) return '0%';
    return Math.round((this.data.statusPublished / this.data.statusTotal) * 100) + '%';
  }

  // Pretty timestamp for "last regenerated"
  lastRegenLabel(): string {
    if (!this.data) return '';
    const ts = this.data.lastUpdated || this.data.lastCreated;
    if (!ts) return 'never generated';
    try {
      const d = new Date(ts.replace(' ', 'T'));
      return d.toLocaleString();
    } catch (e) {
      return ts;
    }
  }

  // ---- Pre-Send Checklist ----
  checklistOpen = false;

  toggleChecklist() {
    this.checklistOpen = !this.checklistOpen;
  }

  checklistItems(): { label: string; pass: boolean; value: string; detail: string }[] {
    if (!this.data) return [];
    const d = this.data;

    const expBlockers = (d.expenseBlockersNullBilledDate || 0) + (d.expenseBlockersNotReviewed || 0);
    const unlocked    = d.unlockedLedgerReservations || 0;
    const missing     = d.missingLedgerRowsReservations || 0;
    const ox          = d.oxAwaitingManualAdd || 0;
    const rezReady    = d.reservationsReadyToAddCount || 0;
    const expReady    = d.expensesReadyToAddCount || 0;
    const total       = d.statusTotal || 0;
    const published   = d.statusPublished || 0;
    const allPublished = total > 0 && published === total;
    const allEmailed   = published > 0 && (d.emailRemaining || 0) === 0;
    const nachaOk      = d.nachaBatchStatus === 'SUBMITTED';
    const runErrors    = (d.lastRunInvariantR7 || 0) + (d.lastRunExpenseErrors || 0);

    return [
      {
        label: 'Expense Blockers',
        pass:  expBlockers === 0,
        value: expBlockers === 0 ? '0 blockers' : `${expBlockers} blocker${expBlockers === 1 ? '' : 's'}`,
        detail: 'Zero expenses with NULL billed date or non-REVIEWED status'
      },
      {
        label: 'Unlocked Reservations',
        pass:  unlocked === 0,
        value: unlocked === 0 ? 'all locked' : `${unlocked} unlocked`,
        detail: 'All reservation ledger rows are locked (eligible for statements)'
      },
      {
        label: 'Missing Ledger Rows',
        pass:  missing === 0,
        value: missing === 0 ? 'none missing' : `${missing} reservation${missing === 1 ? '' : 's'}`,
        detail: 'All reservations have OWNER_AMOUNT and KBM_MANAGEMENT_FEE rows'
      },
      {
        label: 'Cancelled (OX) Revenue',
        pass:  ox === 0,
        value: ox === 0 ? 'all accounted for' : `${ox} qualifying cancellation${ox === 1 ? '' : 's'}`,
        detail: 'All OX cancellations with net retained revenue are on statements'
      },
      {
        label: 'Reservations Added',
        pass:  rezReady === 0,
        value: rezReady === 0 ? 'all pulled in' : `${rezReady} pending add`,
        detail: 'No new reservations are waiting to be pulled into statements'
      },
      {
        label: 'Expenses Added',
        pass:  expReady === 0,
        value: expReady === 0 ? 'all pulled in' : `${expReady} pending add`,
        detail: 'No reviewed expenses are waiting to be pulled into statements'
      },
      {
        label: 'All Statements Published',
        pass:  allPublished,
        value: total === 0 ? 'no statements' : `${published} / ${total} published`,
        detail: 'Every statement for the period has been published'
      },
      {
        label: 'Owner Emails Sent',
        pass:  allEmailed,
        value: published === 0 ? 'nothing published yet' : `${d.emailedCount || 0} / ${published} emailed`,
        detail: 'Every published statement has been emailed to its owner'
      },
      {
        label: 'NACHA Distribution',
        pass:  nachaOk,
        value: d.nachaBatchStatus || 'not generated',
        detail: 'NACHA ACH batch has been submitted to Bank of Hawaii'
      },
      {
        label: 'Last Run Errors',
        pass:  runErrors === 0,
        value: runErrors === 0 ? 'clean run' : `${runErrors} error${runErrors === 1 ? '' : 's'}`,
        detail: 'No R7 invariant violations or expense exceptions in last generation run'
      }
    ];
  }

  checklistPassCount(): number {
    return this.checklistItems().filter(i => i.pass).length;
  }

  checklistAllClear(): boolean {
    const items = this.checklistItems();
    return items.length > 0 && items.every(i => i.pass);
  }

  // ---- Info popover ----
  toggleInfo(key: string, event: MouseEvent) {
    event.stopPropagation();
    Object.keys(this.infoOpen).forEach(k => { if (k !== key) this.infoOpen[k] = false; });
    this.infoOpen[key] = !this.infoOpen[key];
  }
  closeAllInfo() {
    Object.keys(this.infoOpen).forEach(k => this.infoOpen[k] = false);
  }

  // ---- Action: regenerate ----
  regenerateStatementsAction() {
    if (!this.data) return;
    const count = this.data.reservationsReadyToAddCount;
    const stmts = this.data.reservationsReadyToAddStmts;
    this.confirmationService.confirm({
      header: 'Regenerate Statements?',
      message:
        `This will pull ${count} new reservations into ${stmts} non-Published statements ` +
        `for ${this.month}/${this.year}. Published statements are NOT touched. Continue?`,
      acceptLabel: 'Regenerate',
      rejectLabel: 'Cancel',
      accept: () => this.runRegenerate(false)
    });
  }
  regenerateExpensesAction() {
    if (!this.data) return;
    const count = this.data.expensesReadyToAddCount;
    const stmts = this.data.expensesReadyToAddStmts;
    this.confirmationService.confirm({
      header: 'Regenerate Expenses?',
      message:
        `This will pull ${count} new REVIEWED expenses into ${stmts} non-Published statements ` +
        `for ${this.month}/${this.year}. Published statements are NOT touched. Continue?`,
      acceptLabel: 'Regenerate',
      rejectLabel: 'Cancel',
      accept: () => this.runRegenerate(true)
    });
  }
  private runRegenerate(expensesOnly: boolean) {
    this.actionInFlight = true;
    const obs = expensesOnly
      ? this.ownerStmtService.regenerateStmtExpenses(this.month, this.year)
      : this.ownerStmtService.regenerateBulkStatement(this.month, this.year);
    obs.subscribe({
      next: () => {
        this.actionInFlight = false;
        this.messageService.add({
          severity: 'success',
          summary: expensesOnly ? 'Expenses Regenerated' : 'Statements Regenerated',
          detail: 'Dashboard and statement list refreshed.'
        });
        this.fetch();
        this.reloadStatementsList.emit();
      },
      error: (err) => {
        this.actionInFlight = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Regenerate Failed',
          detail: err?.error?.message || 'See console for details.'
        });
      }
    });
  }

  // ---- Action: lock ledgers ----
  lockLedgersAction() {
    if (!this.data) return;
    const n = this.data.unlockedLedgerReservations;
    this.confirmationService.confirm({
      header: 'Lock All Unlocked Ledger Rows?',
      message:
        `This will lock every unlocked ledger row across ${n} reservations in ${this.month}/${this.year}. ` +
        `Once locked, those reservations become eligible for owner statements. ` +
        `Note: this only affects rows that are currently unlocked or NULL. Continue?`,
      acceptLabel: 'Lock All',
      rejectLabel: 'Cancel',
      accept: () => {
        this.actionInFlight = true;
        this.ownerStmtService.dashboardLockLedgers(this.month, this.year).subscribe({
          next: (res: any) => {
            // actionInFlight intentionally left true — runRegenerate() owns it from here
            const r = res?.data ?? res;
            const rowsLocked = r?.rowsLocked ?? 0;
            this.closeDrill(); // close the stale unlocked-reservations list
            if (rowsLocked > 0) {
              this.messageService.add({
                severity: 'info',
                summary: 'Ledger Rows Locked',
                detail: `${rowsLocked} row${rowsLocked === 1 ? '' : 's'} locked. Auto-regenerating statements…`
              });
              this.runRegenerate(false);
            } else {
              this.actionInFlight = false;
              this.messageService.add({
                severity: 'info',
                summary: 'Nothing to Lock',
                detail: 'No unlocked ledger rows found in this period.'
              });
              this.fetch();
            }
          },
          error: (err) => {
            this.actionInFlight = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Lock Failed',
              detail: err?.error?.message || 'See console for details.'
            });
          }
        });
      }
    });
  }

  // ---- Action: bulk set expenses REVIEWED ----
  bulkSetReviewedAction() {
    if (!this.data) return;
    const n = this.data.expenseBlockersNotReviewed;
    this.confirmationService.confirm({
      header: 'Bulk Advance to REVIEWED?',
      message:
        `This will advance ${n} APPROVED expenses (status 5) to REVIEWED (status 6) for ${this.month}/${this.year}. ` +
        `Only expenses with showToStmt=1 and a Statement Billed Date inside the period are affected. ` +
        `After this runs, click "Regenerate Expenses" to add them to statements. Continue?`,
      acceptLabel: 'Advance All',
      rejectLabel: 'Cancel',
      accept: () => {
        this.actionInFlight = true;
        this.ownerStmtService.dashboardSetExpensesReviewed(this.month, this.year).subscribe({
          next: (res: any) => {
            this.actionInFlight = false;
            const r = res?.data ?? res;
            this.messageService.add({
              severity: 'success',
              summary: 'Expenses Advanced to REVIEWED',
              detail: `${r?.expensesReviewed ?? 0} expenses moved to REVIEWED.`
            });
            this.fetch();
          },
          error: (err) => {
            this.actionInFlight = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Bulk Advance Failed',
              detail: err?.error?.message || 'See console for details.'
            });
          }
        });
      }
    });
  }

  // ---- Drill-down dialogs ----
  openExpenseBlockersDrill() {
    if (!this.data) return;
    this.drillTitle = 'Expense Blockers';
    this.drillTileKey = 'expenseBlockers';
    this.drillColumns = [
      { field: 'propertyCode', header: 'Property' },
      { field: 'expenseName', header: 'Expense' },
      { field: 'expenseDate', header: 'Date' },
      { field: 'stmtBilledDate', header: 'Stmt Billed Date' },
      { field: 'statusLabel', header: 'Status' },
      { field: 'totalAMT', header: 'Amount' }
    ];
    this.drillTitle = 'Expense Blockers';
    this.drillTileKey = 'expenseBlockers';
    this.startDrill();
    this.ownerStmtService.dashboardDrillExpenseBlockers(this.month, this.year).subscribe({
      next: (res: any) => {
        const rows: any[] = (res?.data ?? res) as any[];
        this.drillRows = rows.map(r => ({
          ...r,
          statusLabel: this.expenseStatusLabel(r.status),
          stmtBilledDate: r.stmtBilledDate || '⚠ NULL'
        }));
        this.drillLoading = false;
      },
      error: (err: any) => this.drillFailed(err)
    });
  }
  openUnlockedLedgersDrill() {
    if (!this.data) return;
    this.drillTitle = 'Unlocked Reservations';
    this.drillTileKey = 'unlockedLedgers';
    this.drillColumns = [
      { field: 'propertyCode', header: 'Property' },
      { field: 'reservationConfId', header: 'Conf #' },
      { field: 'checkInDateFormatted', header: 'Check-In' },
      { field: 'checkOutDateFormatted', header: 'Check-Out' },
      { field: 'reservationStatus', header: 'Status' },
      { field: 'unlockedRows', header: 'Unlocked / Total' },
      { field: '_orderLink', header: 'Order' }
    ];
    this.startDrill();
    this.ownerStmtService.dashboardDrillUnlockedReservations(this.month, this.year).subscribe({
      next: (res: any) => {
        const rows: any[] = (res?.data ?? res) as any[];
        this.drillRows = rows.map(r => ({
          ...r,
          unlockedRows: `${r.unlockedRows} / ${r.totalRows}`,
          checkInDateFormatted: this.formatDateUS(r.checkInDateTime),
          checkOutDateFormatted: this.formatDateUS(r.checkOutDateTime),
          _orderLink: r.orderId ? `/order/${r.orderId}` : null
        }));
        this.drillLoading = false;
      },
      error: (err: any) => this.drillFailed(err)
    });
  }
  openQualifyingOxDrill() {
    if (!this.data) return;
    this.drillTitle = 'Cancelled (OX) Awaiting — Qualifying Reservations';
    this.drillTileKey = 'oxAwaiting';
    this.drillColumns = [
      { field: 'propertyCode', header: 'Property' },
      { field: 'reservationConfId', header: 'Conf #' },
      { field: 'checkInDateFormatted', header: 'Check-In' },
      { field: 'checkOutDateFormatted', header: 'Check-Out' },
      { field: 'rezTypeLabel', header: 'Type' },
      { field: 'netRetained', header: 'Net Retained ($)' },
      { field: '_orderLink', header: 'Order' }
    ];
    this.startDrill();
    this.ownerStmtService.dashboardDrillQualifyingOx(this.month, this.year).subscribe({
      next: (res: any) => {
        const rows: any[] = ((res?.data ?? res) as any[]) || [];
        this.drillRows = rows.map(r => ({
          ...r,
          checkInDateFormatted: this.formatDateUS(r.checkInDateTime),
          checkOutDateFormatted: this.formatDateUS(r.checkOutDateTime),
          rezTypeLabel: this.rezTypeLabel(r.reservationType),
          _orderLink: r.orderId ? `/order/${r.orderId}` : null,
          // Action state — stored directly on row for clean ngModel binding
          _state: null as ('included' | 'excluded' | null),
          _inFlight: false,
          _error: null as string | null,
          _partialMode: false,
          _partialAmount: null as number | null
        }));
        this.drillLoading = false;
      },
      error: (err: any) => this.drillFailed(err)
    });
  }

  includeOx(row: any) {
    if (row._inFlight || row._state) return;
    row._inFlight = true; row._error = null;
    this.ownerStmtService.addOxReservationToStmt(row.reservationId, this.month, this.year).subscribe({
      next: () => {
        row._inFlight = false; row._state = 'included';
        this.messageService.add({ severity: 'success', summary: 'Added to Statement',
          detail: `${row.propertyCode} — reservation added.` });
        this.fetch();
      },
      error: (err: any) => { row._inFlight = false; row._error = err?.error?.message || 'Failed to add.'; }
    });
  }

  excludeOx(row: any) {
    if (row._inFlight || row._state) return;
    row._inFlight = true; row._error = null;
    this.ownerStmtService.excludeOxRez(row.ownerStmtId, row.reservationId, this.month, this.year).subscribe({
      next: () => {
        row._inFlight = false; row._state = 'excluded';
        this.messageService.add({ severity: 'info', summary: 'Excluded',
          detail: `${row.propertyCode} — excluded from statement.` });
        this.fetch();
      },
      error: (err: any) => { row._inFlight = false; row._error = err?.error?.message || 'Failed to exclude.'; }
    });
  }

  startPartialOx(row: any) {
    row._partialMode = true;
    row._partialAmount = row.netRetained > 0 ? +(row.netRetained.toFixed(2)) : null;
  }

  cancelPartialOx(row: any) {
    row._partialMode = false; row._partialAmount = null; row._error = null;
  }

  confirmPartialOx(row: any) {
    if (row._inFlight || row._state) return;
    if (!row._partialAmount || row._partialAmount <= 0) { row._error = 'Enter a positive amount.'; return; }
    row._inFlight = true; row._error = null;
    this.ownerStmtService.addOxReservationToStmt(row.reservationId, this.month, this.year, row._partialAmount).subscribe({
      next: () => {
        row._inFlight = false; row._state = 'included'; row._partialMode = false;
        this.messageService.add({ severity: 'success', summary: 'Partial Amount Added',
          detail: `${row.propertyCode} — $${(+row._partialAmount).toFixed(2)} added.` });
        this.fetch();
      },
      error: (err: any) => { row._inFlight = false; row._error = err?.error?.message || 'Failed to add partial.'; }
    });
  }

  /** Maps reservationType integer to a human-readable label. */
  rezTypeLabel(type: number | null): string {
    switch (type) {
      case 1:  return 'Owner';
      case 2:  return 'Owner Guest';
      case 3:  return 'Guest';
      case 15: return 'Owner Guest Paid';
      default: return type != null ? String(type) : '—';
    }
  }

  /**
   * Formats a datetime string (e.g. "2026-04-15T00:00:00" or "2026-04-15 00:00:00")
   * to US date format MM/DD/YYYY.  Returns the original value if parsing fails.
   */
  formatDateUS(dt: string | null): string {
    if (!dt) return '';
    try {
      const d = new Date(dt.replace(' ', 'T'));
      if (isNaN(d.getTime())) return dt;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch {
      return dt;
    }
  }

  /** Navigate to the KBMAdmin order page for a given order ID. */
  goToOrder(orderId: number | string) {
    if (!orderId) return;
    this.router.navigate(['/order', orderId]);
  }
  openPendingBillingDrill() {
    if (!this.data) return;
    this.drillTitle = 'Pending Billing — Expenses with NULL Statement Billed Date';
    this.drillTileKey = 'pendingBilling';
    this.drillColumns = [
      { field: 'propertyCode', header: 'Property' },
      { field: 'expenseName', header: 'Expense' },
      { field: 'expenseDate', header: 'Expense Date' },
      { field: 'ageDays', header: 'Age (days)' },
      { field: 'statusLabel', header: 'Status' },
      { field: 'totalAMT', header: 'Amount' }
    ];
    this.startDrill();
    this.ownerStmtService.dashboardDrillPendingBilling().subscribe({
      next: (res: any) => {
        const rows: any[] = (res?.data ?? res) as any[];
        this.drillRows = rows.map(r => ({ ...r, statusLabel: this.expenseStatusLabel(r.status) }));
        this.drillLoading = false;
      },
      error: (err: any) => this.drillFailed(err)
    });
  }

  openMissingLedgerRowsDrill() {
    if (!this.data) return;
    this.drillTitle = 'Reservations Missing OWNER_AMOUNT / KBM_MANAGEMENT_FEE';
    this.drillTileKey = 'missingLedgerRows';
    this.drillColumns = [
      { field: 'propertyCode', header: 'Property' },
      { field: 'reservationConfId', header: 'Conf #' },
      { field: 'checkInDateFormatted', header: 'Check-In' },
      { field: 'checkOutDateFormatted', header: 'Check-Out' },
      { field: 'reservationStatus', header: 'Status' },
      { field: 'missingLabel', header: 'Missing' }
    ];
    this.startDrill();
    this.ownerStmtService.dashboardDrillMissingLedgerRows(this.month, this.year).subscribe({
      next: (res: any) => {
        const rows: any[] = (res?.data ?? res) as any[];
        this.drillRows = rows.map(r => {
          const missing: string[] = [];
          if (!r.hasOwnerAmount) missing.push('OWNER_AMOUNT');
          if (!r.hasMgmtFee)     missing.push('KBM_MANAGEMENT_FEE');
          return {
            ...r,
            missingLabel: missing.join(', '),
            checkInDateFormatted: this.formatDateUS(r.checkInDateTime),
            checkOutDateFormatted: this.formatDateUS(r.checkOutDateTime)
          };
        });
        this.drillLoading = false;
      },
      error: (err: any) => this.drillFailed(err)
    });
  }

  closeDrill() {
    this.drillDialogOpen = false;
    this.drillRows = [];
    this.drillLoading = false;
    this.drillError = null;
  }

  /** Call before every drill API request to reset state and show loading indicator. */
  private startDrill() {
    this.drillRows = [];
    this.drillLoading = true;
    this.drillError = null;
    this.drillDialogOpen = true;
  }

  /** Shared error handler for all drill subscribe() calls. */
  private drillFailed(err: any) {
    this.drillLoading = false;
    this.drillError = (err?.error?.message ?? err?.message ?? 'Failed to load data. Check server logs.');
  }

  // ---- Helpers ----
  expenseStatusLabel(s: number): string {
    return ({
      1: 'DRAFT', 2: 'PENDING', 3: 'IN_PROCESS', 4: 'COMPLETED',
      5: 'APPROVED', 6: 'REVIEWED', 7: 'NEED_ATTENTION'
    } as any)[s] || ('STATUS_' + s);
  }

  onStatusTileClick(status: number) { this.filterByStatus.emit([status]); }
  onUnemailedClick() { this.showOnlyUnemailed.emit(); }

  /** Controls visibility of the NACHA Tools dialog. */
  nachaDialogOpen = false;

  /** Open the NACHA Tools dialog (Tax + Owner Distribution file generation). */
  goToNacha() {
    this.nachaDialogOpen = true;
  }
  goToHelp() {
    window.open('/owner-statements-help', '_blank');
  }
}
